import type { LedgerSummary } from '@taler/api-client'
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalizedGeneric,
  type RouteLocationRaw,
  type Router,
} from 'vue-router'
import { watch } from 'vue'
import { routes } from './routes.ts'
import { hasCapability } from './session.ts'
import { visibleNavItems } from './shell/nav-items.ts'

export interface RouterOptions {
  /** Reads the ledger the guard should judge against; undefined while the session resolves. */
  readonly activeLedger: () => LedgerSummary | undefined
}

/** The first place the role can actually open, for a route it may not open. */
function fallbackRoute(ledger: LedgerSummary): RouteLocationRaw {
  const [first] = visibleNavItems((capability) => hasCapability(ledger, capability))
  return first?.to ?? { name: 'foundation' }
}

function isAllowed(to: RouteLocationNormalizedGeneric, ledger: LedgerSummary | undefined): boolean {
  const access = to.meta.access
  if (access === undefined || to.meta.public === true) return true
  if (ledger === undefined) return true
  return access((capability) => hasCapability(ledger, capability))
}

/**
 * Navigation is filtered by capability so a role never sees an item that 403s.
 * One guard reads `meta.access`; a page never re-implements the check. Switching the
 * active ledger changes what a role may open without a navigation ever happening, so the
 * same check also runs reactively - a route the guard let the previous ledger's role into
 * can strand the new one otherwise.
 */
export function createAppRouter(options: RouterOptions): Router {
  const router = createRouter({ history: createWebHistory(), routes })

  router.beforeEach((to) => {
    const ledger = options.activeLedger()
    return isAllowed(to, ledger) ? true : fallbackRoute(ledger!)
  })

  watch(options.activeLedger, (ledger) => {
    if (ledger === undefined) return
    if (!isAllowed(router.currentRoute.value, ledger)) void router.replace(fallbackRoute(ledger))
  })

  return router
}
